import { useState } from 'react';
import { RefreshCw, PlusCircle, Gavel, RotateCcw, Wallet } from 'lucide-react';

interface AdminSyncPanelProps {
  syncing: boolean;
  onSync?: () => void;
  onResetAndRefresh?: () => void;
  onOpenNewTeam?: () => void;
  onStartAuction?: () => void;
  onCreatePlayerUsers?: () => void;
  creatingPlayerUsers?: boolean;
  onRecalculateBalances?: () => void;
  recalculatingBalances?: boolean;
}

const AdminSyncPanel = ({
  syncing,
  onSync,
  onResetAndRefresh,
  onOpenNewTeam,
  onStartAuction,
  onCreatePlayerUsers,
  creatingPlayerUsers = false,
  onRecalculateBalances,
  recalculatingBalances = false
}: AdminSyncPanelProps) => {
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const handleResetConfirm = () => {
    setShowResetConfirm(false);
    onResetAndRefresh?.();
  };

  return (
    <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-xl p-4 sm:p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div>
        <h2 className="text-lg font-bold">Admin Controls</h2>
        <p className="text-sm text-indigo-200">Sync players, manage teams, and run auctions.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {onSync && (
          <button
            onClick={onSync}
            disabled={syncing}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white text-indigo-700 font-bold px-4 py-2 rounded-lg hover:bg-indigo-50 disabled:opacity-50 text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync WhatsApp'}
          </button>
        )}

        {/* Reset & Refresh with confirmation */}
        {onResetAndRefresh && (
          !showResetConfirm ? (
            <button
              onClick={() => setShowResetConfirm(true)}
              disabled={syncing}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-orange-500 font-bold px-4 py-2 rounded-lg hover:bg-orange-600 disabled:opacity-50 text-sm"
              title="Delete unsold players and re-sync from WhatsApp"
            >
              <RotateCcw className="w-4 h-4" /> Reset & Refresh
            </button>
          ) : (
            <div className="flex gap-1">
              <button
                onClick={handleResetConfirm}
                disabled={syncing}
                className="flex items-center gap-1 bg-red-600 font-bold px-3 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm"
              >
                Confirm Reset
              </button>
              <button
                onClick={() => setShowResetConfirm(false)}
                className="flex items-center gap-1 bg-gray-500 font-bold px-3 py-2 rounded-lg hover:bg-gray-600 text-sm"
              >
                Cancel
              </button>
            </div>
          )
        )}

        {onOpenNewTeam && (
          <></>
        )}
        {onCreatePlayerUsers && (
          <button
            onClick={onCreatePlayerUsers}
            disabled={creatingPlayerUsers}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-emerald-500 font-bold px-4 py-2 rounded-lg hover:bg-emerald-600 text-sm disabled:opacity-60"
          >
            <PlusCircle className={`w-4 h-4 ${creatingPlayerUsers ? 'animate-spin' : ''}`} />
            Player Logins
          </button>
        )}
        {onRecalculateBalances && (
          <button
            onClick={onRecalculateBalances}
            disabled={recalculatingBalances}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-indigo-900 font-bold px-4 py-2 rounded-lg hover:bg-indigo-800 text-sm disabled:opacity-60"
          >
            <Wallet className={`w-4 h-4 ${recalculatingBalances ? 'animate-spin' : ''}`} />
            {recalculatingBalances ? 'Recalculating...' : 'Recalc Balances'}
          </button>
        )}
        {onStartAuction && (
          <button
            onClick={onStartAuction}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-yellow-500 text-yellow-900 font-bold px-4 py-2 rounded-lg hover:bg-yellow-400 text-sm"
          >
            <Gavel className="w-4 h-4" /> Live Auction
          </button>
        )}
      </div>
    </div>
  );
};

export default AdminSyncPanel;
